import React, { useEffect } from 'react';
import { FormInstance } from 'antd/lib/form';
import { Form, Row, Col, Affix } from 'antd';
import styled from 'styled-components';
import { ClientFeatureConfig, JSONTransform } from '../../../types/Feature';
import JsonTransformItem from './JSONTransformItem';
import FormListAddButton from '../../FormListAddButton';

const cardHeaderTitleStyle = { fontSize: '18px' };

interface JSONTransformSectionProps {
    formInstance: FormInstance;
    jsonTransformConfig?: Array<ClientFeatureConfig<JSONTransform>>;
    isDetailMode?: boolean;
    enableControls: boolean;
    // eslint-disable-next-line react/no-unused-prop-types
    resetFormFlag?: boolean;
}

const CodeTag = styled.span`
    padding: 2px 3px;
    margin: 0 5px 0 0;
    background-color: #d5d5d5;
    border-radius: 3px;
`;

const FleetInterface: React.FC = () => {
    const str = `interface Fleet {
    id: number;
    name: string;
    type: string;
    maxCapacity?: number;
    loadingTime?: number;
    pickUpServiceTime?: number;
    dropOffServiceTime?: number;
}`;
    return <pre>{str}</pre>;
};

const PriceMatrixInterface: React.FC = () => {
    const str = `interface PriceMatrix {
    type: string;
    prices: Array<{
        maxWeight: number;
        price: number;
        nextDayPrice: number;
    }>;
}`;
    return <pre>{str}</pre>;
};

const DIExampleInterface: React.FC = () => {
    const str = `
const apiWrapperService = DI.DIContainer.get(DI.TYPES.StuartApiWrapperService);

return new Promise(async (resolve) => {
    const address = await apiWrapperService.getGeocodes([{address: "Pau Claris 186, Barcelona"}]);
    resolve({
        ...payload,
        address
    });
});`;
    return <pre>{str}</pre>;
};

const JsonTransformInstructions: React.FC = () => (
    <div className="font-size--small" style={{ height: '80vh', overflowY: 'auto' }}>
        The function is called with these parameters :
        <br />
        <ul>
            <li>
                {/* eslint-disable-next-line prettier/prettier */}
                <CodeTag>payload</CodeTag>: the request payload or, in the case of the response transformation, the
                resulting payload from processing. Consult the documentation of the calling api (Stuart API or client
                API) for details
            </li>
            <li>
                {/* eslint-disable-next-line prettier/prettier */}
                <CodeTag>queryParams</CodeTag>: the request query parameters
            </li>
            <li>
                {/* eslint-disable-next-line prettier/prettier */}
                <CodeTag>clientConfigs</CodeTag>
                : an object containing (at the moment) two properties
                <br />
                <ul>
                    <li>
                        {/* eslint-disable-next-line prettier/prettier */}
                        <CodeTag>fleets</CodeTag>
                        an array of objects representing the client Fleet config (see other tab) that fits the interface
                        :
                        <FleetInterface />
                    </li>
                    <li>
                        {/* eslint-disable-next-line prettier/prettier */}
                        <CodeTag>priceMatrices</CodeTag>
                        an array of objects representing the client&apos;s Price matrices config (see Price Matrix tab)
                        that fits the following interface :
                        <PriceMatrixInterface />
                    </li>
                </ul>
            </li>
            <li>
                {/* eslint-disable-next-line prettier/prettier */}
                <CodeTag>DI</CodeTag>: an object with two properties
                <br />
                <ul>
                    <li>
                        {/* eslint-disable-next-line prettier/prettier */}
                        <CodeTag>DIContainer</CodeTag>
                        the DIContainer with all our services
                    </li>
                    <li>
                        {/* eslint-disable-next-line prettier/prettier */}
                        <CodeTag>TYPES</CodeTag>
                        the TYPES object with the symbols of all our services
                    </li>
                </ul>
                <br />
                Keep in mind that in order to use <CodeTag>await</CodeTag> you have to use{' '}
                <CodeTag>new Promise</CodeTag>, for example:
                <br />
                <DIExampleInterface />
            </li>
            <li>
                {/* eslint-disable-next-line prettier/prettier */}
                <CodeTag>path</CodeTag>: the request path
            </li>
        </ul>
    </div>
);

const JSONTransformSection: React.FC<JSONTransformSectionProps> = (props: JSONTransformSectionProps) => {
    const { isDetailMode, jsonTransformConfig, enableControls, formInstance } = props;

    useEffect(() => {
        if (jsonTransformConfig) {
            formInstance.setFields([
                {
                    name: 'jsonTransform',
                    value: jsonTransformConfig.map((elem) => {
                        const { method } = elem.config;
                        return {
                            jsonTransformFunction: elem.config.transformFunction,
                            jsonTransformEnabled: elem.config.enabled,
                            jsonTransformTarget: elem.config.target,
                            jsonTransformHTTPMethod: !method ? 'any' : method,
                            jsonTransformPath: elem.config.path?.value,
                            jsonPathMatchingMethod: elem.config.path?.matching,
                            featureId: elem.featureId,
                            id: elem.id,
                        };
                    }),
                },
            ]);
        }
    }, [jsonTransformConfig]);

    return (
        <div className="card border-0" id="json-transform-section">
            <div className="card-header border-bottom d-flex justify-content-between align-items-center bg-white">
                <p className="m-0 p-0 h5" style={cardHeaderTitleStyle}>
                    JSON Transform
                </p>
            </div>
            <Row>
                <i className="my-3">
                    You can add a JSON transformation on any endpoint you wish (leave &quot;path&quot; empty to apply on
                    every endpoint).
                </i>
            </Row>
            <Row gutter={24}>
                <Col span={14} md={{ span: 14 }}>
                    <Form.List name="jsonTransform">
                        {(fields, { add, remove }): JSX.Element => (
                            <>
                                {fields.map((field) => (
                                    <JsonTransformItem
                                        key={field.key}
                                        jsonTransformConfig={jsonTransformConfig?.[field.key]}
                                        isDetailMode={isDetailMode}
                                        enableControls={enableControls}
                                        fieldKey={field.key}
                                        fieldName={field.name}
                                        remove={remove}
                                    />
                                ))}
                                <FormListAddButton
                                    isDetailMode={isDetailMode || false}
                                    add={add}
                                    text="Add a JSON transform function"
                                />
                            </>
                        )}
                    </Form.List>
                </Col>
                <Col span={10} md={{ span: 10 }}>
                    <Affix offsetTop={30}>
                        <JsonTransformInstructions />
                    </Affix>
                </Col>
            </Row>
        </div>
    );
};

export default JSONTransformSection;
